import { useContext, useMemo } from 'react';
import { Button, Flex, Tabs, Title } from '@mantine/core';
import { generatePath } from 'react-router-dom';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { MantineTab } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.tsx';
import { StyledTabsList } from '@dealstack/frontend/shared/components/MantineTabs/styled.tsx';
import { PageContainer } from '@dealstack/frontend/shared/layout/PageContainer/PageContainer.tsx';
import { QuickSwitchBreadcrumbsNav } from '@dealstack/frontend/shared/components/QuickSwitchBreadcrumbsNav/QuickSwitchBreadcrumbsNav.tsx';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils.tsx';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { DealEventLogActions } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogActions/DealEventLogActions.tsx';
import { DealEventLogTable } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventLogTable.tsx';
import { useDealEventLogTable } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventLogTable.ts';
import { useDealEventLogTab } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventLogTab.ts';
import { useMarkDealEventLogViewed } from '@dealstack/frontend/app/pages/Project/DealEventLog/useMarkDealEventLogViewed.ts';
import {
	getDealEventLogTabs,
	parseDealEventLogTab,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogTabs.utils.ts';
import { EventLogCard } from './DealEventLog.styles';

export const DealEventLog = () => {
	const tCapManage = useTranslationCapManage();
	const tCommonUi = useTranslationCommonUi();
	const { dealId } = useRequiredParams('dealId');
	const { deal, deals } = useContext(DealContext);
	const { tab: activeTab, setTab } = useDealEventLogTab();

	const title = tCapManage('deal_event_log.title');

	const breadcrumbs = useMemo(
		() => [
			...makeDealPageBreadcrumbs({ tCommonUi, dealId, dealName: deal?.name, deals }),
			{
				name: title,
				url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.EVENT_LOG}`, { dealId }),
			},
		],
		[tCommonUi, dealId, deal?.name, deals, title]
	);

	useBreadcrumbs(breadcrumbs);

	const tabs = useMemo(() => getDealEventLogTabs(tCapManage), [tCapManage]);

	const feed = useDealEventLogTable(dealId, activeTab);

	useMarkDealEventLogViewed(dealId);

	return (
		<PageContainer style={{ overflow: 'hidden' }}>
			<QuickSwitchBreadcrumbsNav />

			<Title fz={32} fw={600} lh={1} c="dark.8">
				{title}
			</Title>

			<EventLogCard>
				<Tabs
					variant="pills"
					c="dark.4"
					color="blue.0"
					value={activeTab}
					onChange={(value) => {
						const next = parseDealEventLogTab(value);

						if (next && next !== activeTab) {
							feed.resetFilters();
							setTab(next);
						}
					}}
					h="100%"
					w="100%"
					display="flex"
					style={{ flexDirection: 'column', overflow: 'hidden' }}
				>
					<div className="deal-event-tab-rows">
						<StyledTabsList $skipTabsBorder $skipTabsPadding>
							{tabs.map((tab) => (
								<MantineTab key={`tab-${tab.value}`} {...tab} />
							))}
						</StyledTabsList>

						<DealEventLogActions
							showFilters={feed.showColumnFilters}
							onToggleFilters={() =>
								feed.setShowColumnFilters(!feed.showColumnFilters)
							}
						/>
					</div>

					<div className="deal-event-panels">
						<Tabs.Panel value={activeTab} className="deal-event-panel">
							<div className="deal-event-table-scroll">
								<DealEventLogTable
									columns={feed.columns}
									rows={feed.rows}
									dataOperations={feed.dataOperations}
									showColumnFilters={feed.showColumnFilters}
									onShowColumnFiltersChange={feed.setShowColumnFilters}
									isLoading={feed.isLoading}
								/>
							</div>

							{feed.hasNextPage && (
								<Flex justify="center" p="0.75rem" className="deal-event-more">
									<Button
										variant="subtle"
										loading={feed.isFetchingNextPage}
										onClick={() => void feed.fetchNextPage()}
									>
										{tCapManage('deal_event_log.load_more')}
									</Button>
								</Flex>
							)}
						</Tabs.Panel>
					</div>
				</Tabs>
			</EventLogCard>
		</PageContainer>
	);
};
